import { useGraphQLQuery } from '@opengov/bnp-core';
import { IStoryWithContent } from '@opengov/stories-shared/types';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  GetStoryWithContentForCustomCssQuery,
  GetStoryWithContentForCustomCssQueryVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { useToast } from '../../../Toast/context/useToast.context';

import { publicationCustomCssQueryKeys } from './publicationCustomCssQueryKeys';

const GetStoryWithContentForCustomCssDocument = graphql(`
  query GetStoryWithContentForCustomCss($storyId: String!) {
    stories(where: { _id: { _eq: $storyId } }) {
      _id
      title
      description
      state
      customCssCode
      contents {
        _id
        active
        content
        contentHtml
        createdAt
        description
        entityId
        format
        group
        layout
        order
        storyId
        title
        type
        updatedAt
        authorId
        author {
          _id
          email
          firstName
          lastName
          opengovId
        }
        contentChartConfig {
          id
          contentId
          storyId
          config
          type
        }
      }
    }
  }
`);

export type UseStoryWithContentParams = { storyId: string } | null;

export function useStoryWithContent(
  params: UseStoryWithContentParams,
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useGraphQLQuery<
    GetStoryWithContentForCustomCssQuery,
    GetStoryWithContentForCustomCssQueryVariables,
    IStoryWithContent | null
  >({
    queryKey: params
      ? publicationCustomCssQueryKeys.storyWithContent(params.storyId)
      : ([...publicationCustomCssQueryKeys.all, 'storyWithContent', 'disabled'] as const),
    document: GetStoryWithContentForCustomCssDocument,
    url: HASURA_SERVICE_URL,
    variables: params ? { storyId: params.storyId } : { storyId: '' },
    options: {
      enabled: params !== null,
      refetchOnMount: 'always',
      onError: () => {
        toggleToast({ title: errorMessage, severity: 'error' });
      },
      select: (data: GetStoryWithContentForCustomCssQuery): IStoryWithContent | null => {
        const story = data.stories?.[0];
        if (!story) return null;
        // Codegen types every nullable column as `T | null`; IStoryWithContent
        // declares them more loosely. Runtime shape matches once Hasura
        // returns a row.
        return story as unknown as IStoryWithContent;
      }
    }
  });
}
